<script setup>
import { ref, watch, onMounted, onUnmounted, nextTick } from 'vue'
import Events from '@/api/Events'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  field: {
    type: String,
    required: true
  },
  query: {
    type: String,
    default: '*'
  },
  logTypes: {
    type: Array,
    default: () => []
  },
  anchorEl: {
    type: Object,
    default: null
  },
  getSelectionState: {
    type: Function,
    default: null
  },
  ctrlActive: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['add-filter', 'close', 'navigate-aggregations', 'select-value'])

// State
const loading = ref(false)
const error = ref(null)
const terms = ref([])
const cardinality = ref(null)
const popoverRef = ref(null)
const position = ref({ top: 0, left: 0 })

// Fetch aggregations when shown
watch(() => props.show, async (isShown) => {
  if (isShown && props.field) {
    // Set initial position immediately at anchor
    if (props.anchorEl) {
      const anchorRect = props.anchorEl.getBoundingClientRect()
      position.value = { top: anchorRect.bottom, left: anchorRect.left }
    }
    await fetchAggregations()
    await nextTick()
    updatePosition()
  }
}, { immediate: true })

// Update position when anchor changes
watch(() => props.anchorEl, async (newAnchor) => {
  if (props.show && newAnchor) {
    // Set initial position at anchor immediately
    const anchorRect = newAnchor.getBoundingClientRect()
    position.value = { top: anchorRect.bottom, left: anchorRect.left }
    // Refine position after DOM updates
    await nextTick()
    updatePosition()
  }
})

// Watch field changes to refetch when switching columns
watch(() => props.field, async (newField, oldField) => {
  if (newField && newField !== oldField && props.show) {
    await fetchAggregations()
    await nextTick()
    updatePosition()
  }
})

// Calculate popover position
function updatePosition() {
  if (!props.anchorEl || !popoverRef.value) return

  const anchorRect = props.anchorEl.getBoundingClientRect()
  const popoverRect = popoverRef.value.getBoundingClientRect()
  const viewportHeight = window.innerHeight
  const viewportWidth = window.innerWidth

  // Default: position below the header
  let top = anchorRect.bottom
  let left = anchorRect.left

  // If popover would go off bottom, position above
  if (top + popoverRect.height > viewportHeight - 20) {
    top = anchorRect.top - popoverRect.height
  }

  // If popover would go off right, align to right edge
  if (left + popoverRect.width > viewportWidth - 20) {
    left = viewportWidth - popoverRect.width - 20
  }

  // Ensure not off left
  if (left < 20) {
    left = 20
  }

  position.value = { top, left }
}

// Fetch aggregation data
async function fetchAggregations() {
  loading.value = true
  error.value = null
  terms.value = []
  cardinality.value = null

  try {
    const aggregations = ['terms', 'cardinality']

    const response = await Events.aggregate(
      props.query,
      props.logTypes,
      props.field,
      aggregations,
      10 // Top 10 values
    )

    if (response.status === 'success' && response.data) {
      terms.value = response.data.terms || []
      cardinality.value = response.data.cardinality
    } else {
      error.value = response.message || 'Failed to load aggregations'
    }
  } catch (err) {
    error.value = 'Failed to load aggregations'
    console.error('Aggregation error:', err)
  } finally {
    loading.value = false
  }
}

// Add filter from + button click
function addFilter(value) {
  emit('add-filter', {
    field: props.field,
    value: value,
    operator: 'equals'
  })
}

// Handle term row click (ctrl-click to select)
function handleTermClick(event, value) {
  if (event.ctrlKey || event.metaKey) {
    event.preventDefault()
    event.stopPropagation()
    emit('select-value', { field: props.field, value })
  }
}

// Navigate to aggregations view with this field
function navigateToAggregations() {
  emit('navigate-aggregations', props.field)
  emit('close')
}

// Format large numbers
function formatCount(count) {
  if (count >= 1000000) {
    return (count / 1000000).toFixed(1) + 'M'
  }
  if (count >= 1000) {
    return (count / 1000).toFixed(1) + 'K'
  }
  return count.toLocaleString()
}

// Handle click outside
function handleClickOutside(event) {
  if (popoverRef.value && !popoverRef.value.contains(event.target)) {
    emit('close')
  }
}

// Handle resize
function handleResize() {
  if (props.show) {
    updatePosition()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  window.addEventListener('resize', handleResize)
  window.addEventListener('scroll', handleResize, true)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('scroll', handleResize, true)
})
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="show"
        ref="popoverRef"
        class="column-stats-popover"
        :style="{ top: position.top + 'px', left: position.left + 'px' }"
        @click.stop
        @mouseleave="$emit('close')"
      >
        <!-- Header -->
        <div class="popover-header">
          <span class="field-name">{{ field }}</span>
          <button class="close-btn" @click="$emit('close')">
            <font-awesome-icon :icon="['fas', 'xmark']" />
          </button>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <div class="spinner-small"></div>
          <span>Loading...</span>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-state">
          <span>{{ error }}</span>
        </div>

        <!-- Content -->
        <template v-else>
          <!-- Top Values -->
          <div class="section">
            <div class="section-header">Top Values</div>
            <div v-if="terms.length === 0" class="empty-state">
              No values found
            </div>
            <div v-else class="terms-list" :class="{ 'ctrl-mode': ctrlActive }">
              <div
                v-for="term in terms"
                :key="term.value"
                class="term-item"
                :class="{
                  'term-selected-field': getSelectionState && getSelectionState(field, term.value) === 1,
                  'term-selected-value': getSelectionState && getSelectionState(field, term.value) === 2
                }"
                @click="handleTermClick($event, term.value)"
              >
                <span class="term-value" :title="term.value">{{ term.value }}</span>
                <span class="term-count">{{ formatCount(term.count) }}</span>
                <button class="filter-btn" title="Add as filter" @click.stop="addFilter(term.value)">
                  <font-awesome-icon :icon="['fas', 'plus']" />
                </button>
              </div>
            </div>
          </div>

          <!-- Cardinality -->
          <div
            v-if="cardinality !== null"
            class="section cardinality-section"
            @click="navigateToAggregations"
          >
            <span class="cardinality-value">{{ formatCount(cardinality) }}</span>
            <span class="cardinality-label">unique values</span>
            <font-awesome-icon class="cardinality-arrow" :icon="['fas', 'arrow-right']" />
          </div>
        </template>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.column-stats-popover {
  position: fixed;
  z-index: 1000;
  min-width: 260px;
  max-width: 320px;
  background: #1E252F;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.75rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  overflow: hidden;
}


/* Header */
.popover-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.03);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.field-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: #E5E7EB;
  font-family: 'Cutive Mono', monospace;
}

.close-btn {
  background: none;
  border: none;
  color: #6B7280;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  transition: all 0.15s ease;
}

.close-btn:hover {
  color: #E5E7EB;
  background: rgba(255, 255, 255, 0.1);
}

/* Loading & Error States */
.loading-state,
.error-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem 1rem;
  color: #6B7280;
  font-size: 0.85rem;
}

.error-state {
  color: #EF4444;
}

.spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid #374151;
  border-top: 2px solid #7357D2;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Sections */
.section {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.section:last-child {
  border-bottom: none;
}

.section-header {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6B7280;
  margin-bottom: 0.5rem;
}

/* Terms List */
.terms-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.term-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.5rem;
  border-radius: 0.375rem;
  transition: all 0.15s ease;
}

.term-item:hover {
  background: rgba(255, 255, 255, 0.04);
}

/* Ctrl mode */
.terms-list.ctrl-mode .term-item {
  cursor: pointer;
  user-select: none;
}

.terms-list.ctrl-mode .term-item:hover {
  background: rgba(115, 87, 210, 0.15);
}

.term-item.term-selected-field {
  background: rgba(115, 87, 210, 0.12);
  border-left: 3px solid #7357D2;
  padding-left: calc(0.5rem - 3px);
}

.term-item.term-selected-value {
  background: rgba(245, 158, 11, 0.12);
  border-left: 3px solid #F59E0B;
  padding-left: calc(0.5rem - 3px);
}

.term-value {
  flex: 1;
  font-size: 0.8rem;
  color: #E5E7EB;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: 'Cutive Mono', monospace;
}

.term-count {
  font-size: 0.75rem;
  color: #9CA3AF;
  font-weight: 500;
}

.filter-btn {
  background: none;
  border: none;
  color: #6B7280;
  cursor: pointer;
  padding: 0.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  transition: all 0.15s ease;
  font-size: 0.7rem;
}

.filter-btn:hover {
  color: #7357D2;
  background: rgba(115, 87, 210, 0.2);
}

/* Cardinality Section */
.cardinality-section {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.cardinality-section:hover {
  background: rgba(115, 87, 210, 0.1);
}

.cardinality-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #7357D2;
}

.cardinality-label {
  font-size: 0.8rem;
  color: #6B7280;
  flex: 1;
}

.cardinality-arrow {
  font-size: 0.75rem;
  color: #6B7280;
  opacity: 0;
  transition: all 0.15s ease;
}

.cardinality-section:hover .cardinality-arrow {
  opacity: 1;
  color: #7357D2;
}

/* Empty State */
.empty-state {
  color: #6B7280;
  font-size: 0.8rem;
  font-style: italic;
  padding: 0.5rem 0;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.expand-enter-active,
.expand-leave-active {
  transition: all 0.2s ease;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
}

.expand-enter-to,
.expand-leave-from {
  max-height: 200px;
}
</style>
